/* eslint-disable jsx-a11y/anchor-is-valid */
import LogoImg from "@assets/img/logo/logo-simple.png";
import {
  FaFileSignature,
  FaFingerprint,
  FaHeadset,
  FaNewspaper,
  FaWallet,
} from "react-icons/fa6";

import React, { useEffect, useState } from "react";
import { FaFolderOpen, FaHandsHelping, FaHome } from "react-icons/fa";

import { useSidebarCollapse } from "@hooks/contexts/SidebarCollapseContext";

import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { NavLink, useLocation } from "react-router-dom";
import { config } from "config";

// Submenu configuration with routes
const SUBMENU_CONFIG = {
  history: {
    routes: [
      "/financial/history/withdraw",
      "/financial/history/deposits",
      "/financial/history/earnings",
    ],
  },
  raffle: {
    routes: ["/raffles/default/list"],
  },
  users: {
    routes: ["/customer/list", "/client/list"],
  },
  campaigns: {
    routes: ["/campaign/default/list"],
  },
};

type SubmenuKey = keyof typeof SUBMENU_CONFIG;

export default function NavbarLeft() {
  const location = useLocation();
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();
  const { isCollapsed, toggleSidebar, handleMouseEnter, handleMouseLeave } =
    useSidebarCollapse();

  // Track which submenu is open
  const [openSubmenu, setOpenSubmenu] = useState<SubmenuKey | null>(null);

  /**
   * Check if current route matches any of the provided paths
   */
  const isRouteActive = (paths: readonly string[]): boolean => {
    return paths.some(
      (path) => path !== "#" && location.pathname.startsWith(path),
    );
  };

  /**
   * Toggle submenu - close others when opening a new one
   */
  const toggleSubmenu = (
    e: React.MouseEvent<HTMLAnchorElement>,
    menuKey: SubmenuKey,
  ) => {
    e.preventDefault();
    setOpenSubmenu((prev) => (prev === menuKey ? null : menuKey));
  };

  /**
   * Show maintenance toast notification
   */
  const showMaintenanceToast = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    pushToastify({
      message: `Página em construção, por favor, tente novamente mais tarde.`,
      type: "info",
      theme: "dark",
    });
    handleNavItemClick();
  };

  /**
   * Close sidebar on mobile when clicking a nav item
   */
  const handleNavItemClick = () => {
    // Check if we're on mobile (screen width < 768px)
    if (window.innerWidth < 768 && !isCollapsed) {
      toggleSidebar();
    }
  };

  /**
   * Auto-open submenu based on current route, or close all if no match
   */
  useEffect(() => {
    const activeMenu = (
      Object.entries(SUBMENU_CONFIG) as unknown as [
        SubmenuKey,
        { routes: string[] },
      ][]
    ).find(([, config]) => isRouteActive(config.routes));

    if (activeMenu) {
      setOpenSubmenu(activeMenu[0]);
    } else {
      // Close all submenus if current route doesn't match any submenu
      setOpenSubmenu(null);
    }
  }, [location.pathname]);

  return (
    <>
      <div
        className={`sidebar ${isCollapsed ? "collapsed" : ""}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        data-background-color="dark"
      >
        <div className="sidebar-logo">
          <div className="logo-header" data-background-color="dark">
            <NavLink to="/" className="logo" onClick={handleNavItemClick}>
              <img
                src={LogoImg}
                alt="navbar brand"
                className="navbar-brand"
                height="50"
              />
            </NavLink>
            <div className="d-flex flex-column p-1 text-white">
              <span>{config.appName}</span>
            </div>
            <div className="nav-toggle">
              <button
                className="btn btn-toggle toggle-sidebar"
                onClick={toggleSidebar}
              >
                <i className="gg-menu-right"></i>
              </button>
            </div>
          </div>
        </div>

        <div className="sidebar-wrapper scrollbar scrollbar-inner">
          <div className="sidebar-content">
            <ul className="nav nav-secondary">
              {/* Dashboard */}
              <li className="nav-item">
                <NavLink
                  to="/dashboard"
                  className="link"
                  onClick={handleNavItemClick}
                >
                  <div className="icon">
                    <FaHome />
                  </div>
                  <p>Dashboard</p>
                </NavLink>
              </li>

              {/* Explore Section */}
              <li className="nav-section">
                <span className="sidebar-mini-icon">
                  <i className="fa fa-ellipsis-h"></i>
                </span>
                <h4 className="text-section">Explorar</h4>
              </li>

              {/* Users Submenu */}
              <li
                className={`nav-item ${
                  isRouteActive(SUBMENU_CONFIG.users.routes) ? "active" : ""
                } ${openSubmenu === "users" ? "submenu" : ""}`}
              >
                <a
                  data-bs-toggle="collapse"
                  href="#users"
                  onClick={(e) => toggleSubmenu(e, "users")}
                >
                  <div className="icon">
                    <FaFileSignature />
                  </div>
                  <p>Usuários</p>
                  <span className="caret"></span>
                </a>
                <div
                  className={`collapse ${openSubmenu === "users" ? "show" : ""}`}
                  id="users"
                >
                  <ul className="nav nav-collapse">
                    <li
                      className={`link ${
                        isRouteActive(["/customer/list"]) ? "active" : ""
                      }`}
                    >
                      <NavLink to="/customer/list" onClick={handleNavItemClick}>
                        <span className="sub-item">Parceiros</span>
                      </NavLink>
                    </li>
                    <li className="link">
                      <a href="#" onClick={showMaintenanceToast}>
                        <span className="sub-item">Clientes</span>
                      </a>
                    </li>
                  </ul>
                </div>
              </li>

              {/* Campaigns Submenu */}
              <li
                className={`nav-item ${
                  isRouteActive(SUBMENU_CONFIG.campaigns.routes) ? "active" : ""
                } ${openSubmenu === "campaigns" ? "submenu" : ""}`}
              >
                <a
                  data-bs-toggle="collapse"
                  href="#campaigns"
                  onClick={(e) => toggleSubmenu(e, "campaigns")}
                >
                  <div className="icon">
                    <FaFileSignature />
                  </div>
                  <p>Campanhas</p>
                  <span className="caret"></span>
                </a>
                <div
                  className={`collapse ${openSubmenu === "campaigns" ? "show" : ""}`}
                  id="campaigns"
                >
                  <ul className="nav nav-collapse">
                    <li
                      className={`link ${
                        isRouteActive(["/campaign/default/list"])
                          ? "active"
                          : ""
                      }`}
                    >
                      <NavLink
                        to="/campaign/default/list"
                        onClick={handleNavItemClick}
                      >
                        <span className="sub-item">Tradicionais</span>
                      </NavLink>
                    </li>
                    <li className="link">
                      <a href="#" onClick={showMaintenanceToast}>
                        <span className="sub-item">De Centavos</span>
                      </a>
                    </li>
                  </ul>
                </div>
              </li>

              {/* Property Section */}
              <li className="nav-section">
                <span className="sidebar-mini-icon">
                  <i className="fa fa-ellipsis-h"></i>
                </span>
                <h4 className="text-section">Propriedade</h4>
              </li>

              {/* Raffles Submenu */}
              <li
                className={`nav-item ${
                  isRouteActive(SUBMENU_CONFIG.raffle.routes) ? "active" : ""
                } ${openSubmenu === "raffle" ? "submenu" : ""}`}
              >
                <a
                  data-bs-toggle="collapse"
                  href="#raffles"
                  onClick={(e) => toggleSubmenu(e, "raffle")}
                >
                  <div className="icon">
                    <FaFileSignature />
                  </div>
                  <p>Campanhas</p>
                  <span className="caret"></span>
                </a>
                <div
                  className={`collapse ${openSubmenu === "raffle" ? "show" : ""}`}
                  id="raffles"
                >
                  <ul className="nav nav-collapse">
                    <li
                      className={`link ${
                        isRouteActive(["/raffles/default/list"]) ? "active" : ""
                      }`}
                    >
                      <NavLink
                        to="/raffles/default/list"
                        onClick={handleNavItemClick}
                      >
                        <span className="sub-item">Tradicionais</span>
                      </NavLink>
                    </li>
                    <li className="link">
                      <a href="#" onClick={showMaintenanceToast}>
                        <span className="sub-item">De Centavos</span>
                      </a>
                    </li>
                  </ul>
                </div>
              </li>

              <li className="nav-section">
                <span className="sidebar-mini-icon">
                  <i className="fa fa-ellipsis-h"></i>
                </span>
              </li>

              {/* Financial Section */}
              <li className="nav-section">
                <span className="sidebar-mini-icon">
                  <i className="fa fa-ellipsis-h"></i>
                </span>
                <h4 className="text-section">Financeiro</h4>
              </li>

              {/* General Statement */}
              <li
                className={`nav-item ${
                  isRouteActive(["/financial/general"]) ? "active" : ""
                }`}
              >
                <NavLink
                  to="/financial/general"
                  className="link"
                  onClick={handleNavItemClick}
                >
                  <div className="icon">
                    <FaWallet />
                  </div>
                  <p>Extrato Geral</p>
                </NavLink>
              </li>

              {/* History Submenu */}
              <li
                className={`nav-item ${
                  isRouteActive(SUBMENU_CONFIG.history.routes) ? "active" : ""
                } ${openSubmenu === "history" ? "submenu" : ""}`}
              >
                <a
                  data-bs-toggle="collapse"
                  href="#history"
                  onClick={(e) => toggleSubmenu(e, "history")}
                >
                  <div className="icon">
                    <FaFolderOpen />
                  </div>
                  <p>Históricos</p>
                  <span className="caret"></span>
                </a>
                <div
                  className={`collapse ${openSubmenu === "history" ? "show" : ""}`}
                  id="history"
                >
                  <ul className="nav nav-collapse">
                    <li
                      className={`link ${
                        isRouteActive(["/history/withdraw"]) ? "active" : ""
                      }`}
                    >
                      <a href="#" onClick={showMaintenanceToast}>
                        <span className="sub-item">Saques</span>
                      </a>
                    </li>
                    <li
                      className={`link ${
                        isRouteActive(["/history/deposits"]) ? "active" : ""
                      }`}
                    >
                      <a href="#" onClick={showMaintenanceToast}>
                        <span className="sub-item">Depósitos</span>
                      </a>
                    </li>
                    <li className="link">
                      <a href="#" onClick={showMaintenanceToast}>
                        <span className="sub-item">Pagamentos</span>
                      </a>
                    </li>
                    <li
                      className={`link ${
                        isRouteActive(["/financial/history/earnings"])
                          ? "active"
                          : ""
                      }`}
                    >
                      <NavLink
                        to="/financial/history/earnings"
                        onClick={handleNavItemClick}
                      >
                        <span className="sub-item">Meus Proventos</span>
                      </NavLink>
                    </li>
                    <li className="link">
                      <a href="#" onClick={showMaintenanceToast}>
                        <span className="sub-item">Minha Assinatura</span>
                      </a>
                    </li>
                  </ul>
                </div>
              </li>

              <li className="nav-section">
                <span className="sidebar-mini-icon">
                  <i className="fa fa-ellipsis-h"></i>
                </span>
              </li>

              {/* Settings Section */}
              <li className="nav-section">
                <span className="sidebar-mini-icon">
                  <i className="fa fa-ellipsis-h"></i>
                </span>
                <h4 className="text-section">Configurações</h4>
              </li>

              {/* My Account */}
              <li
                className={`nav-item ${
                  isRouteActive(["/account"]) ? "active" : ""
                }`}
              >
                <NavLink
                  to="/account"
                  className="link"
                  onClick={handleNavItemClick}
                >
                  <div className="icon">
                    <FaFingerprint />
                  </div>
                  <p>Minha Conta</p>
                </NavLink>
              </li>

              {/* Help Section */}
              <li className="nav-section">
                <span className="sidebar-mini-icon">
                  <i className="fa fa-ellipsis-h"></i>
                </span>
                <h4 className="text-section">AJUDA</h4>
              </li>

              {/* FAQ */}
              <li className="nav-item">
                <a href="#" onClick={showMaintenanceToast}>
                  <div className="icon">
                    <FaHandsHelping />
                  </div>
                  <p>F.A.Q.</p>
                </a>
              </li>

              {/* Support Center */}
              <li className="nav-item">
                <a href="#" onClick={showMaintenanceToast}>
                  <div className="icon">
                    <FaHeadset />
                  </div>
                  <p>Central</p>
                </a>
              </li>

              {/* Terms */}
              <li className="nav-item">
                <a href="#" onClick={showMaintenanceToast}>
                  <div className="icon">
                    <FaNewspaper />
                  </div>
                  <p>Termos</p>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}
